'use client'

/* Fullscreen gallery lightbox for the masjid detail page (GALLERY ONLY — the hero
   never triggers it). View-layer overlay over the EXISTING gallery image list:
   uploaded photos[] OR Google /api/masjids/[id]/photo?index=N&w=1600 (same URLs the
   gallery renders). No data/backend changes, no browser storage — React state only. */

import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ChevronLeft, ChevronRight, X } from 'lucide-react'

interface PhotoLightboxProps {
  photos: string[]
  startIndex: number
  open: boolean
  onClose: () => void
  /** true when the list is Google-sourced (drives the attribution line + fail-soft) */
  google?: boolean
  /** "Photos via Google — {names}" credit, reused from the page's googleCredit */
  credit?: string | null
  /** branded fallback on image error */
  placeholderSrc: string
  altBase: string
}

const SWIPE_THRESHOLD = 40 // px of horizontal travel before a swipe flips the photo

export default function PhotoLightbox({
  photos, startIndex, open, onClose, google, credit, placeholderSrc, altBase,
}: PhotoLightboxProps) {
  const [mounted, setMounted] = useState(false)
  const [index, setIndex] = useState(startIndex)
  const dialogRef = useRef<HTMLDivElement>(null)
  const closeBtnRef = useRef<HTMLButtonElement>(null)
  const touchStartX = useRef<number | null>(null)

  const count = photos.length
  const multi = count > 1

  // Portal target only exists after mount (SSR-safe).
  useEffect(() => { setMounted(true) }, [])

  // Re-anchor to the clicked thumbnail each time the lightbox opens.
  useEffect(() => { if (open) setIndex(startIndex) }, [open, startIndex])

  // Nav WRAPS around both ends (loops); no-op when there's a single photo.
  const prev = useCallback(() => { if (multi) setIndex((i) => (i - 1 + count) % count) }, [multi, count])
  const next = useCallback(() => { if (multi) setIndex((i) => (i + 1) % count) }, [multi, count])

  // Lock body scroll while open; restore the prior value on close/unmount.
  useEffect(() => {
    if (!open) return
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prevOverflow }
  }, [open])

  // Keyboard: Escape closes, arrows navigate, Tab is trapped inside the dialog.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose() }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); prev() }
      else if (e.key === 'ArrowRight') { e.preventDefault(); next() }
      else if (e.key === 'Tab') {
        // Focus trap — cycle among the dialog's focusable controls only.
        const focusables = dialogRef.current?.querySelectorAll<HTMLElement>('button')
        if (!focusables || !focusables.length) return
        const first = focusables[0]
        const last = focusables[focusables.length - 1]
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus() }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus() }
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose, prev, next])

  // Move focus into the overlay on open (accessibility).
  useEffect(() => { if (open) closeBtnRef.current?.focus() }, [open])

  if (!open || !mounted || !count) return null

  const src = photos[index]

  return createPortal(
    <div
      role="presentation"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(6, 10, 22, 0.94)',
        display: 'flex', flexDirection: 'column',
        backdropFilter: 'blur(2px)',
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={`${altBase} — photo viewer`}
        style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column' }}
      >
        {/* Top bar: counter + close */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', color: '#fff', flex: '0 0 auto' }}>
          <span aria-live="polite" style={{ fontSize: 13, fontWeight: 600, letterSpacing: '0.02em', opacity: 0.9 }}>
            {index + 1} / {count}
          </span>
          <button
            ref={closeBtnRef}
            type="button"
            aria-label="Close photo viewer"
            onClick={(e) => { e.stopPropagation(); onClose() }}
            style={{ ...ctrlStyle, width: 42, height: 42 }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Stage: image is CONTAIN (portrait photos never cropped). Clicking the
            image does NOT close; clicking the surrounding backdrop does. */}
        <div
          style={{ flex: '1 1 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', minHeight: 0, padding: '0 8px' }}
          onTouchStart={(e) => { touchStartX.current = e.touches[0]?.clientX ?? null }}
          onTouchEnd={(e) => {
            const start = touchStartX.current
            touchStartX.current = null
            if (start == null) return
            const dx = (e.changedTouches[0]?.clientX ?? start) - start
            if (dx > SWIPE_THRESHOLD) prev()
            else if (dx < -SWIPE_THRESHOLD) next()
          }}
        >
          {multi && (
            <button
              type="button"
              aria-label="Previous photo"
              onClick={(e) => { e.stopPropagation(); prev() }}
              style={{ ...ctrlStyle, position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', width: 46, height: 46 }}
            >
              <ChevronLeft size={26} />
            </button>
          )}

          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={src}
            alt={`${altBase} — photo ${index + 1}`}
            onClick={(e) => e.stopPropagation()}
            onError={(e) => { (e.currentTarget as HTMLImageElement).src = placeholderSrc }}
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 8, userSelect: 'none', boxShadow: '0 12px 48px rgba(0,0,0,0.5)' }}
          />

          {multi && (
            <button
              type="button"
              aria-label="Next photo"
              onClick={(e) => { e.stopPropagation(); next() }}
              style={{ ...ctrlStyle, position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', width: 46, height: 46 }}
            >
              <ChevronRight size={26} />
            </button>
          )}
        </div>

        {/* Attribution — only for Google-sourced photos, mirrors the gallery line. */}
        {google && credit ? (
          <div style={{ flex: '0 0 auto', textAlign: 'center', padding: '10px 18px 18px', color: 'rgba(255,255,255,0.72)', fontSize: 12 }}>
            Photos via Google — {credit}
          </div>
        ) : (
          <div style={{ flex: '0 0 auto', height: 12 }} />
        )}
      </div>
    </div>,
    document.body,
  )
}

const ctrlStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  borderRadius: 999, border: '1px solid rgba(255,255,255,0.22)',
  background: 'rgba(20, 26, 42, 0.6)', color: '#fff', cursor: 'pointer',
  transition: 'background 0.15s ease',
}
